import {computed, ref} from 'vue';
import {organizationDetailsEndpoints} from '@configuration';
import {useOrganizationMemberStore} from '@features/team/members/organization-member.store';
import {defineStore, storeToRefs} from 'pinia';
import {useAuth} from '#imports';
import {readonlyState} from '@malt/utils';
import type {OrganizationDetailsResource} from '@api';

export const useOrganizationDetailsStore = defineStore('organization-details-store', () => {
    const organizationId = ref<string>();
    const organizationDetails = ref<OrganizationDetailsResource>();

    const {user} = storeToRefs(useAuth());
    const organizationMemberStore = useOrganizationMemberStore();
    const {loggedInUserOrganisationRole} = storeToRefs(organizationMemberStore);

    const canEdit = computed(() => loggedInUserOrganisationRole.value === 'SYSTEM_ADMIN' || !!user.value?.platformAdmin);

    async function init(orgaId: string) {
        organizationId.value = orgaId;
        organizationDetails.value = await organizationDetailsEndpoints.getOrganizationDetails({organizationId: orgaId});
    }

    async function updateOrganizationDetails(organizationName: string) {
        if (!organizationId.value) {
            throw new Error('Organization ID is not set');
        }

        organizationDetails.value = await organizationDetailsEndpoints.updateOrganizationDetails({
            organizationId: organizationId.value,
            organizationDetailsUpdateRequest: {organizationName},
        });
    }

    async function uploadOrganizationLogo(file: File) {
        if (!organizationId.value) {
            throw new Error('Organization ID is not set');
        }

        organizationDetails.value = await organizationDetailsEndpoints.uploadOrganizationLogo({
            organizationId: organizationId.value,
            file,
        });
    }

    return {
        init,
        canEdit,
        updateOrganizationDetails,
        uploadOrganizationLogo,
        organizationDetails: readonlyState(organizationDetails),
        organizationId: readonlyState(organizationId),
    };
});
